import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';

import { DynamicGrid} from '../../models/dynamic.model';
import { LazyLoadEvent, MessageService } from 'primeng/api';
import { NavigationService } from '../../services/navigation.service';
import { BaseListComponent } from '../../models/base/base-list.component';
import { ListService } from '../../services/list.service';
import { FormService } from '../../services/form.service';
import { ActivatedRoute } from '@angular/router';
import { RowTools, RowToolEvent } from '../../models/row-tools.model';
import { SharedService } from '../../services/shared.service';
import { AccountService } from '../../services/account.service';
import { Subject, takeUntil } from 'rxjs';
import * as Sentry from '@sentry/angular';

@Component({
  selector: 'location-list',
  templateUrl: 'location-list.component.html',
  providers: [
    ListService,
    FormService,
  ],
  styles: [`
        :host ::ng-deep .ui-table .ui-table-thead > tr > th {
            position: -webkit-sticky;
            position: sticky;
            top: 0;
            background: #EBEBEB;
            border-bottom: 1px solid #D8D8D8;
        }
`]
})
export class LocationListComponent extends BaseListComponent implements OnInit, OnDestroy {

  showUserModal: boolean = false;
  showOrderModal: boolean = false;
  private destroy$ = new Subject<void>();

  @ViewChild("orderDialog", { static: false }) dialogRef: ElementRef;
  prevLazyLoadEvent: LazyLoadEvent | null = null;

  constructor(
    protected messageService: MessageService,
    private listService: ListService,
    private navigationService: NavigationService,
    private sharedService: SharedService,
    private formService: FormService,
    private accountService: AccountService,
    private route: ActivatedRoute) {
    super();
  }

  ngOnInit() {
    this.isLoading = true;
    this.title = 'Locations';
    this.class = 'location';

    this.model = new DynamicGrid<any>();

    this.navigationService.showTopNav(true);
    this.navigationService.setTitle(this.title);
    this.navigationService.setLeftNav(null, 'locations');
    this.navigationService.showButton(true);
    this.navigationService.showFilter(true, true);

    this.rowTools = new RowTools();

    if (this.sharedService.getPermissions('locations_edit') || this.sharedService.getPermissions('locations')) {
      this.rowTools.addLink('edit', 'View');
    }
    if (this.sharedService.getPermissions('can_delete')) {
      this.rowTools.addKebob('delete', 'Delete');
    }
    this.subs['clickLocationButton'] = this.navigationService.clickNewLocation$.subscribe(s => {
      this.add();
    });

    this.subs['clickOrderButton'] = this.navigationService.clickNewOrder$.subscribe(s => {
      this.addOrder();
    });

    this.subs['clickUserButton'] = this.navigationService.clickNewUser$.subscribe(s => {
      this.addUser();
    });

    this.subs['filter'] = this.navigationService.filter$.subscribe(s => {
      if(this.query != s){
        this.first = 0;
      }
      this.prevLazyLoadEvent.first = 0;
      this.query = s;
      this.load();
    });

    this.route.queryParams.subscribe(p => {
      if (p['preview'] != null && p['preview'] !== '') {
        this.view(p['preview']);
      }
      if (p['show'] != null && p['show'] !== '') {
        this.edit({ id: p['show'] });
      }
    });

  }

  ngOnDestroy() {
    this.safeUnsub('clickLocationButton');
    this.safeUnsub('clickUserButton');
    this.safeUnsub('clickOrderButton');
    this.safeUnsub('filter');
    this.destroy$.next();
    this.destroy$.complete();
  }

  lazyLoad(e: any) {
    if (this.prevLazyLoadEvent && this.sharedService.isSameEvent(e, this.prevLazyLoadEvent)) {
      return;
    }
    setTimeout(() => {
      this.prevLazyLoadEvent = e;
      this.setGridState(e);
      this.load();
    }, 0);
  }

  load() {
    this.isLoading = true;
    this.model.rows = [];
    this.rowCount = this.first == 0 ? 0 : this.rowCount;
    this.listService.locations(this.params, this.query, this.first, this.rows, this.sortField, this.sortOrder)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.loadColumns(res.body.metadata);
        this.model.rows = res.body.results;
        this.rowCount = res.body.count;
        this.isLoading = false;
        this.navigationService.setTitleAndSub({ title: this.title, subtitle: this.rowCount + ' total' });
      });

  }

  cancelDelete() {
    this.isDeleting = false;
  }

  clickRowTools(event: RowToolEvent) {
    this.selection = event.item;
    switch (event.key) {
      case "delete":
        this.isDeleting = true;
        break;
      case "edit":
        this.edit(event.item);
        break;
      case 'view':
        this.view(event.item?.guid);
        break;
    }
  }


  view(id: string) {
    this.navigationService.setDetail('location', id);
  }

  rowChangePreview(id: string) {
    if (this.navigationService.getNav().showRightNav) {
      this.navigationService.setRightNav('location', id);
    }
  }

  edit(row: any) {
    this.selection = row;
    this.showModal = true;
  }

  delete() {
    this.formService
      .delete("location", this.selection.guid)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          this.isDeleting = false;
          this.messageService.add(
            this.toast(true, "Location deleted successfully.")
          );

          this.selection = null;
          this.sharedService
            .getMenuCount("tenderslocations")
            .subscribe((res) => {
              this.navigationService.menuCountChange(
                res.body,
                "tenderslocations"
              );
            });
          this.load();
        },
        (err) => {
          let msg = "Error occurred";
          if (err && err.error && err.error.message) {
            this.isDeleting = false;
            msg = err.error.message;
          }
          this.messageService.add(this.toast(false, msg));
          console.error(err);
        }
      );
  }

  complete() {
    this.cancel();
    this.load();
  }

  add() {
    this.selection = null;
    this.showModal = true;
    this.showUserModal = false;
    this.showOrderModal = false;
    this.isAdding = true;
  }

  addUser() {
    this.showUserModal = true;
    this.showModal = false;
    this.showOrderModal = false;
  }

  addOrder() {
    this.showUserModal = false;
    this.showModal = false;
    this.showOrderModal = true;
  }

  cancel(event?: any) {
    if (typeof event === 'boolean' && event === false) {
      this.showOrderModal = true;
      return;
    }
    this.showOrderModal = false;
    let orderIdsToUnlock: number[] = [];

    if (event && Array.isArray(event) && event.length > 0) {
      orderIdsToUnlock = event;
    }

    if (orderIdsToUnlock.length > 0) {
      this.accountService.setAllowOrdersEdit(orderIdsToUnlock)
        .subscribe({
          error: (error) => {
            Sentry.captureException(new Error('Failed to unlock orders: ' + orderIdsToUnlock.join(', ')), { extra: { error } });
          }
        });
    }

    this.showModal = false;
    this.showUserModal = false;
    this.isEditing = false;
    this.isAdding = false;
    this.isDeleting = false;
  }


}
